<script setup>
import { useRouter, useRoute } from 'vue-router';
import { usePaymentsStore } from '@/store/modules/payments';
import { useNamingPayment } from '@/composables/saju-insight/useNamingPayment';
import {
  useNamingPurchaseHistory,
  buildNamingSelectionsFromHistoryItem,
} from '@/composables/saju-insight/useNamingPurchaseHistory';
import { DEV_MODE } from '@/config/dev-mode';
import NamingCover from '@/components/saju-insight/nangman-naming/NamingCover.vue';
import NamingServiceSelect from '@/components/saju-insight/nangman-naming/NamingServiceSelect.vue';
import CoverPanelNaming from '@/components/saju-insight/nangman-naming/CoverPanelNaming.vue';
import DetailPageNaming from '@/components/saju-insight/nangman-naming/DetailPageNaming.vue';
import NamingPaymentPopup from '@/components/saju-insight/nangman-naming/NamingPaymentPopup.vue';
import ConfirmComponentPay from '@/components/saju-insight/common/confirmComponent.vue';
import AlertComponent from '@/components/common/Alert.vue';
import AnalysisLoadingOverlay from '@/components/saju-insight/common/AnalysisLoadingOverlay.vue';
import PopAnalysisInfo from '@/components/saju-insight/common/PopAnalysisInfo.vue';
import { useAnalysisSajuContext } from '@/composables/saju-insight/useAnalysisSajuContext';
import { addContentLogAPI } from '@/api/logServiceApi';


const router = useRouter();
const route = useRoute();
const paymentsStore = usePaymentsStore();
const props = defineProps({ saju: { type: Object, required: true } });
const context = useAnalysisSajuContext();
const formKey = ref(0);
const initializing = ref(true);
const restoringHistory = ref(false);
let flowEpoch = 0;

/**
 * NamingServiceSelect의 선택값 — DetailPage 전달 + 옵션 매칭(가격/티켓)에 사용.
 * 옵션 매칭에 쓰이므로 useNamingPayment보다 먼저 선언해 ref로 주입한다.
 */
const namingSelections = ref({
  type: null,         // 'recommend' | 'analyze'
  target: null,       // 'person' | 'pet' | 'biz' | 'culture'
  detail: null,       // 'kor' | 'han' 등
  detailInput: null,  // PopNamingDetailInput 결과
  saju: props.saju,         // 작명 기준 사주
  analysisLogId: null, // 결제/기구매 식별자 — 컨텐츠 평가(objectKey)
});

const {
  analysisTypeData,
  fetchAnalysisType,
  pointPrice,
  isPointActive,
  isTicketActive,
  namingTicketCount,
  popAnalysisType,
  submitPayment,
  isPaymentProcessing,
} = useNamingPayment(namingSelections);

const {
  fetchAll: fetchAllPurchaseHistory,
  resetCache: resetPurchaseCache,
} = useNamingPurchaseHistory();

/** 보유 작명분석권 총합 — 표지(NamingCover)·우측 패널(CoverPanelNaming) 뱃지용.
 *  선택 옵션과 무관한 전체 보유량. 옵션별 보유수(namingTicketCount)는 결제 판단·결제 팝업 전용으로,
 *  옵션이 채워진 상태에서도 표지 뱃지가 0건으로 떨어지지 않도록 총합을 직접 참조한다. */
const ticketCount = computed(() => paymentsStore.namingTicketCount ?? 0);

const toggleTabOpenClass = ref('');
const toggleTabContent = () => {
  toggleTabOpenClass.value = toggleTabOpenClass.value === '' ? 'wide' : '';
};

const isReady = ref(false);
const isDetail = ref(false);

// =============== 결제 상태 ===============
const showPaymentPopup = ref(false);
const showPopAnalysisInfo = ref(false);
const buyType = ref('');
const dialogConfirm = ref(null);
const dialogAlert = ref(null);
const confirmTitle = ref('');
const confirmTitle2 = ref('');
const confirmText = ref('');
const confirmNextUrl = ref(null);
const confirmDoUrl = ref(null);
const warningTxt = ref('');
const alertTitle = ref('');
const alertText2 = ref('');
const alertNextUrl2 = ref(null);

const goToDetail = async () => {
  await openPaymentPopup();
};

const goToOverView = () => {
  flowEpoch += 1;
  showPaymentPopup.value = false;
  dialogConfirm.value?.dismissFromBack?.();
  isDetail.value = false;
  isReady.value = false;
  formKey.value += 1;
  const query = { ...route.query, menu: '/overviewNangmanNaming' };
  delete query.analysisLogId;
  router.push({ path: route.path, query });
  // 새 이름 짓기 — 이전 구매내역 선택 컨텍스트 초기화 (구매내역 카드 활성 표시 해제)
  namingSelections.value = {
    type: null,
    target: null,
    detail: null,
    detailInput: null,
    saju: props.saju,
    analysisLogId: null,
  };
};

const onSelectHistory = (item) => {
  if (!context.isActive()) return;
  flowEpoch += 1;
  const selections = buildNamingSelectionsFromHistoryItem(item);
  if (!selections) return;
  if (Number(item.guestUserId) !== Number(props.saju.guestUserId)) {
    context.openHistory('naming', item.guestUserId, item.analysisLogId);
    return;
  }
  namingSelections.value = { ...selections, saju: props.saju };
  if (String(route.query.analysisLogId) !== String(item.analysisLogId)) {
    context.openHistory('naming', props.saju.guestUserId, item.analysisLogId);
  }
  // DetailPageNaming에서 analysisLogId에 해당하는 기존 결과를 조회한다.
  isDetail.value = true;
  // 기구매 항목 결과 1페이지 진입 (log_card_open과 별개로 결과 조회 시작을 통일 기록)
  addContentLogAPI({ contentType: 'ap_naming_page_opt3', option3: '1' });
  toggleTabOpenClass.value = '';
};

// =============== 결제 흐름 ===============
const openPaymentPopup = async () => {
  if (!isReady.value || !context.isActive()) return;
  const epoch = flowEpoch;
  addContentLogAPI({ contentType: 'ap_naming_btn_start_detail' });
  await Promise.all([
    paymentsStore.getOwnPoint(),
    paymentsStore.fetchTicketCount(),
    fetchAnalysisType(),
  ]);

  if (!context.isActive() || epoch !== flowEpoch) return;
  if (!isPointActive.value && !isTicketActive.value) {
    showPopAnalysisInfo.value = true;
    return;
  }
  if (isTicketActive.value) {
    buyType.value = 'ticket';
    goConfirm();
    return;
  }
  buyType.value = 'point';
  showPaymentPopup.value = true;
};

const closePaymentPopup = () => {
  showPaymentPopup.value = false;
};

const onSelectPaymentMethod = (method) => {
  buyType.value = method;
  goConfirm();
};

const handleClosePop = () => {
  showPopAnalysisInfo.value = false;
};

const onRecharge = (shortageAmount) => {
  router.push({ path: '/purchase/points', query: { next: route.fullPath, shortageAmount } });
};

const handleChargePop = (shortageAmount) => {
  showPopAnalysisInfo.value = false;
  onRecharge(shortageAmount);
};

const goConfirm = () => {
  if (!buyType.value) return;
  showPaymentPopup.value = false;

  confirmTitle.value = '선택한 결제방법으로';
  confirmTitle2.value = '낭만작명을 시작합니다.';
  confirmText.value = '사용';
  confirmNextUrl.value = onConfirmPayment;
  confirmDoUrl.value = onConfirmPayment;
  warningTxt.value =
    buyType.value === 'point'
      ? `포인트 ${pointPrice.value.toLocaleString()} P 사용`
      : '분석권 1건 사용';
  dialogConfirm.value?.openConfirmDialog();
};

const onConfirmPayment = async () => {
  if (!context.isActive() || !isReady.value || isPaymentProcessing.value) return;
  const epoch = flowEpoch;
  const selection = { ...namingSelections.value };
  addContentLogAPI({ contentType: 'ap_naming_payment_opt3', option3: '결제확정' });
  try {
    if (!analysisTypeData.value) await fetchAnalysisType();
    if (!context.isActive() || epoch !== flowEpoch) return;
    const payResult = await submitPayment({
      type: selection.type,
      target: selection.target,
      detail: selection.detail,
      detailInput: selection.detailInput,
      saju: props.saju,
      paymentMethod: buyType.value,
    });
    if (!context.isActive() || epoch !== flowEpoch) return;
    // 컨텐츠 평가(objectKey)용 analysisLogId 보관 — 신규 결제 응답에서 추출
    namingSelections.value.analysisLogId = payResult?.analysisLogId ?? null;
    DEV_MODE && console.log('[낭만작명 PC] 결제 결과 analysisLogId:', payResult?.analysisLogId, payResult);

    alertTitle.value = '성공적으로 결제되었습니다.';
    alertText2.value = '';
    alertNextUrl2.value = onPaymentSuccess;
    dialogAlert.value?.openAlertDialog();
  } catch (e) {
    if (!context.isActive() || epoch !== flowEpoch) return;
    DEV_MODE && console.error('[낭만작명 PC] 결제 실패:', e);
    alertTitle.value =
      e?.response?.data?.detail || e?.response?.data?.message || '결제에 실패했습니다.';
    alertText2.value = '';
    alertNextUrl2.value = null;
    dialogAlert.value?.openAlertDialog();
  }
};

const onPaymentSuccess = async () => {
  const epoch = flowEpoch;
  // 포인트(낙관적 차감 없음)·분석권 재조회 → CreditBar 갱신
  await Promise.all([paymentsStore.getOwnPoint(), paymentsStore.fetchTicketCount()]);
  if (!context.isActive() || epoch !== flowEpoch) return;
  resetPurchaseCache();
  await fetchAllPurchaseHistory();
  if (!context.isActive() || epoch !== flowEpoch) return;
  // DetailPageNaming에서 analysisLogId에 해당하는 기존 결과를 조회한다.
  isDetail.value = true;
  if (namingSelections.value.analysisLogId) context.openHistory('naming', props.saju.guestUserId, namingSelections.value.analysisLogId);
  // 결제 직후 결과 1페이지 진입 — 기구매 조회(onSelectHistory)와 동일하게 조회 시작을 기록
  addContentLogAPI({ contentType: 'ap_naming_page_opt3', option3: '1' });
  toggleTabOpenClass.value = '';
};

const restoreHistory = async (id) => {
  if (!id) {
    restoringHistory.value = false;
    if (isDetail.value) {
      flowEpoch += 1;
      isDetail.value = false;
      isReady.value = false;
      formKey.value += 1;
      namingSelections.value = { type: null, target: null, detail: null, detailInput: null, saju: props.saju, analysisLogId: null };
    }
    return;
  }
  restoringHistory.value = true;
  try {
    const history = await fetchAllPurchaseHistory();
    if (!context.isActive() || String(route.query.analysisLogId) !== String(id)) return;
    const item = history.find((entry) => String(entry.analysisLogId) === String(id));
    if (item) onSelectHistory(item);
    else {
      alertTitle.value = '작명 이용내역을 찾을 수 없습니다.';
      dialogAlert.value?.openAlertDialog();
    }
  } finally {
    if (String(route.query.analysisLogId) === String(id)) restoringHistory.value = false;
  }
};
watch(() => route.query.analysisLogId, restoreHistory);

onMounted(async () => {
  try {
    await Promise.all([
      paymentsStore.getOwnPoint(),
      paymentsStore.fetchTicketCount(),
      fetchAnalysisType(),
    ]);
    if (context.isActive()) await restoreHistory(route.query.analysisLogId);
  } finally {
    initializing.value = false;
  }
});
</script>

<template>
  <div class="analysis-main" :class="toggleTabOpenClass">
    <template v-if="!isDetail">
      <div class="si-overview si-overview--naming">
        <NamingCover :ticket-count="ticketCount" />
        <NamingServiceSelect
          :key="formKey"
          :fixed-saju="saju"
          @ready-change="isReady = $event"
          @update:selected-type="namingSelections.type = $event"
          @update:selected-target="namingSelections.target = $event"
          @update:selected-detail="namingSelections.detail = $event"
          @update:detail-input="namingSelections.detailInput = $event"
          @update:selected-saju="namingSelections.saju = saju"
        />
      </div>
    </template>
    <DetailPageNaming
      v-else
      :type="namingSelections.type"
      :target="namingSelections.target"
      :detail="namingSelections.detail"
      :detail-input="namingSelections.detailInput"
      :saju="namingSelections.saju"
      :analysis-log-id="namingSelections.analysisLogId"
      @go-to-over-view="goToOverView"
    />
  </div>

  <div
    :class="['analysis-desc', 'overview-payment-info', toggleTabOpenClass]"
  >
    <CoverPanelNaming
      :mode="isDetail ? 'detail' : 'cover'"
      :ready="isReady"
      :ticket-count="ticketCount"
      :active-log-id="namingSelections.analysisLogId"
      :guest-user-id="saju.guestUserId"
      @start="goToDetail"
      @reset="goToOverView"
      @select-history="onSelectHistory"
    />
  </div>

  <div
    class="btn-fold-toggle"
    :class="[toggleTabOpenClass, { 'overview-mode': !isDetail }]"
  >
    <button
      class="btn"
      type="button"
      :aria-expanded="toggleTabOpenClass !== 'wide'"
      :aria-label="toggleTabOpenClass === 'wide' ? '이용 내역 패널 펼치기' : '이용 내역 패널 접기'"
      :title="toggleTabOpenClass === 'wide' ? '이용 내역 패널 펼치기' : '이용 내역 패널 접기'"
      @click="toggleTabContent()"
    />
  </div>

  <AnalysisLoadingOverlay
    :loading="initializing || restoringHistory"
    message="낭만작명 분석중입니다!"
    contained
  />

  <!-- 결제 수단 선택 팝업 -->
  <NamingPaymentPopup
    :is-visible="showPaymentPopup"
    :analysis-type="popAnalysisType"
    :ticket-count="namingTicketCount"
    :is-point-active="isPointActive"
    :is-ticket-active="isTicketActive"
    @close="closePaymentPopup"
    @select-method="onSelectPaymentMethod"
  />

  <!-- 결제 확인 다이얼로그 -->
  <ConfirmComponentPay
    ref="dialogConfirm"
    :title="confirmTitle"
    :title2="confirmTitle2"
    :text="confirmText"
    :warning="warningTxt"
    :next-url="confirmNextUrl"
    :do-url="confirmDoUrl"
  />

  <!-- 결제 결과 알림 -->
  <AlertComponent
    ref="dialogAlert"
    :title="alertTitle"
    :text="alertText2"
    :next-url="alertNextUrl2"
  />

  <!-- 포인트 부족 + 분석권 없음 → 포인트 충전 안내 -->
  <Teleport to="body">
    <PopAnalysisInfo
      :is-visible="showPopAnalysisInfo"
      :analysis-type="popAnalysisType"
      @close="handleClosePop"
      @charge="handleChargePop"
    />
  </Teleport>
</template>

<style lang="scss">
@import '@/assets/nangman_style/_saju-insight-overview.scss';
</style>
